/**
 * Price engine — pure, dependency-free (03-architecture.md §Price engine).
 * All timestamps are unix seconds. Money is integer VND.
 *
 *   price(l, t)  = floorTo1000(start − (start − floor) × clamp((t − start_at) / duration, 0, 1))
 *   rate(l)      = (start − floor) / duration                    (VND/s, interpolation only)
 *   bucket(l, t) = by seconds remaining: <1h ending_soon, <6h hours, <24h under_day, else days
 *
 * Buyers only ever receive `segment()`; owners and admins receive `l`.
 */
import {
  BUCKET_ENDING_SOON_S,
  BUCKET_HOURS_S,
  BUCKET_UNDER_DAY_S,
  DROP_DOW,
  DROP_LOCAL_TIME_S,
  DROP_PERIOD_S,
  DROP_WINDOW_S,
  INTERPOLATE_MAX_S,
  PRICE_STEP,
  TZ_OFFSET_S,
} from "./limits.ts";

export type PriceParams = {
  start_price: number;
  floor_price: number;
  start_at: number;
  duration_s: number;
};

export type Bucket = "ending_soon" | "hours" | "under_day" | "days";

export type ListingStatus = "draft" | "scheduled" | "active" | "sold_out" | "expired" | "cancelled";

/** What a buyer sees. Never carries start_at, end_at, floor or duration. */
export type Segment = {
  price: number;
  rate_per_s: number;
  server_ts: number;
  bucket: Bucket;
  qty_remaining: number;
  status: ListingStatus;
};

export function floorToStep(v: number, step: number = PRICE_STEP): number {
  return Math.floor(v / step) * step;
}

export function endAt(l: Pick<PriceParams, "start_at" | "duration_s">): number {
  return l.start_at + l.duration_s;
}

/** Exact integer math: (start − floor) × elapsed / duration is computed in BigInt so a
 *  value that is mathematically on a 1,000 boundary never lands one VND under it. */
export function price(l: PriceParams, t: number): number {
  const elapsed = Math.trunc(t) - l.start_at;
  if (elapsed <= 0) return l.start_price;
  if (elapsed >= l.duration_s) return l.floor_price;
  const span = BigInt(l.start_price - l.floor_price);
  const drop = (span * BigInt(elapsed)) / BigInt(l.duration_s); // truncates toward zero, all ≥ 0
  return floorToStep(l.start_price - Number(drop));
}

export function rate(l: PriceParams): number {
  return (l.start_price - l.floor_price) / l.duration_s;
}

export function bucket(l: Pick<PriceParams, "start_at" | "duration_s">, t: number): Bucket {
  const remaining = endAt(l) - t;
  if (remaining < BUCKET_ENDING_SOON_S) return "ending_soon";
  if (remaining < BUCKET_HOURS_S) return "hours";
  if (remaining < BUCKET_UNDER_DAY_S) return "under_day";
  return "days";
}

export function segment(
  l: PriceParams,
  t: number,
  extra: { qty_remaining: number; status: ListingStatus },
): Segment {
  return {
    price: price(l, t),
    rate_per_s: rate(l),
    server_ts: t,
    bucket: bucket(l, t),
    qty_remaining: extra.qty_remaining,
    status: extra.status,
  };
}

// ---------------------------------------------------------------- Drop math
// Asia/Ho_Chi_Minh is a fixed UTC+7 with no DST, so "local" is a constant shift.
// 1970-01-01 (day 0 in the shifted frame) was a Thursday → dow = (day + 4) % 7.

/** The most recent Drop instant ≤ t. */
export function prevDrop(t: number): number {
  const local = Math.trunc(t) + TZ_OFFSET_S;
  const day = Math.floor(local / 86_400);
  const dow = (day + 4) % 7;
  const daysBack = (dow - DROP_DOW + 7) % 7;
  let candidate = (day - daysBack) * 86_400 + DROP_LOCAL_TIME_S - TZ_OFFSET_S;
  if (candidate > t) candidate -= DROP_PERIOD_S;
  return candidate;
}

/**
 * The next Drop instant strictly after `now`. With `minLead`, a Drop closer
 * than that is skipped (R-LST-3: within 2 h before a Drop → the one after).
 */
export function nextDrop(now: number, minLead: number = 0): number {
  let candidate = prevDrop(now) + DROP_PERIOD_S;
  if (candidate - now < minLead) candidate += DROP_PERIOD_S;
  return candidate;
}

export function isDropInstant(t: number): boolean {
  return prevDrop(t) === t;
}

/** Sat 20:00 ≤ t < Sun 20:00 local: the only time the 6 h preset is offered (R-LST-2). */
export function isDropWindow(t: number): boolean {
  return t - prevDrop(t) < DROP_WINDOW_S;
}

// ------------------------------------------------------- client-side helpers

/** Offset to add to the client clock to approximate the server clock. */
export function clockOffset(serverTs: number, clientNow: number): number {
  return serverTs - clientNow;
}

/**
 * Client interpolation between polls: price − rate × elapsed, re-floored to
 * 1,000, elapsed clamped to [0, INTERPOLATE_MAX_S] so a backgrounded tab or a
 * skewed clock never runs the price toward zero. `now` must already be in
 * server time (see clockOffset).
 */
export function interpolate(
  seg: Pick<Segment, "price" | "rate_per_s" | "server_ts" | "status">,
  now: number,
): number {
  if (seg.status !== "active") return seg.price;
  const elapsed = Math.min(Math.max(now - seg.server_ts, 0), INTERPOLATE_MAX_S);
  return Math.max(0, floorToStep(seg.price - seg.rate_per_s * elapsed));
}
